import { MAIN_STYLE, MAIN_STYLE_GROUPS, PADDING_SIDES, SELECTION_EVENTS } from '../shared.js'
export const dropdown = {

    displayName: 'Dropdown',

    // LVGL-style parts — label/exportSuffix are the source of truth for the
    // Property Editor's Accordion titles and for legacy JSONL wire keys
    // (see v2/shared.js part metadata doc and v2/wireFormat.js toWireKey).
    parts: {
        main: {
            label: 'Main',
            exportSuffix: '',
            order: 0,
            propertyGroups: [...MAIN_STYLE_GROUPS],
            properties: {
                ...MAIN_STYLE,
                text_color: { type: 'color' },
                text_font:  { type: 'int', min: 8 },
                text_font_family: { type: 'string', internal: true },
                padding:    { type: 'int', min: 0, exportSpecial: true },
                ...PADDING_SIDES,
            },
        },
        // No LVGL 9 counterpart — OpenHASP-specific styling hook for the
        // dropdown list's row buttons (see v2/shared.js part metadata doc).
        item_buttons: {
            label: 'Item Buttons',
            exportSuffix: '40',
            order: 1,
            // Dropdown rows are flat item surfaces; do not expose or export
            // the generic background-gradient properties for this part.
            supportsBackgroundGradient: false,
            properties: {
                bg_color:     { type: 'color' },
                text_color:   { type: 'color' },
                radius:       { type: 'int', min: 0, max: 999 },
                border_width: { type: 'int', min: 0 },
                border_color: { type: 'color' },
                padding:      { type: 'int', min: 0, exportSpecial: true },
            },
        },
        selected: {
            label: 'Selected',
            exportSuffix: '50',
            order: 2,
            // The selected row is part of the item list and follows the same
            // flat-background rule as item_buttons.
            supportsBackgroundGradient: false,
            properties: {
                bg_color: { type: 'color' },
            },
        },
        scrollbar: {
            label: 'Scrollbar',
            exportSuffix: '80',
            order: 3,
            properties: {
                bg_color: { type: 'color' },
            },
        },
    },

    properties: {
        x: { type: 'int' }, y: { type: 'int' },
        width: { type: 'int', min: 0 }, height: { type: 'int', min: 0 },
        min_width: { type: 'int', min: 0 }, min_height: { type: 'int', min: 0 },
        haspid:   { type: 'int', min: 1, max: 254, internal: true },
        parentid: { type: 'int', min: 0, max: 254, internal: true },
        enabled: { type: 'bool' }, hidden: { type: 'bool' },
        val:           { type: 'int' },
        text:          { type: 'string', readOnly: true },
        options:       { type: 'string' },
        list_options:  { type: 'bool', internal: true },
        direction:     { type: 'int', min: 0, max: 3 },
        show_selected: { type: 'bool' },
        max_height:    { type: 'int', min: 0 },
        entityid:      { type: 'string', internal: true },
        // Designer-only preview toggle simulating the open/closed appearance of
        // the real firmware widget. Never exported — see OBJECT_PROP_SKIP.
        previewExpanded: { type: 'bool' },
        // Font Family selection for the closed row, list items, and the
        // expand/collapse chevron — all three share this widget's single
        // TEXT_FONT slot (see label.js's `font` property comment for the full
        // wire-format semantics).
        font: { type: 'resource', resourceType: 'font', label: 'Font Family', inline: true },
    },

    defaults: {
        properties: {
            x: 0, y: 0, width: 160, height: 180, min_width: 100, min_height: 10,
            enabled: true, hidden: false,
            max_height: 200,
            // Real firmware defaults (C struct initializers) — wire-safe either
            // way since firmware falls back to its own defaults when unset, but
            // without these the canvas preview doesn't match real firmware.
            direction: 0, show_selected: true,
            options: 'Option1\nOption2\nOption3',
            // Existing projects load without this field and must keep rendering
            // the always-expanded appearance they had before this feature existed.
            previewExpanded: true,
            font: '',
        },
        styles: {
            // padding drives text layout in drawOptions; item_buttons.padding
            // drives item list layout
            main: { padding: 0 },
            item_buttons: { padding: 0 },
            // scrollbar.bg_color has no entry in any theme file — dropdown is
            // the only widget using this part, so there's no existing per-theme
            // convention to match. Same missing-default bug as line_rounded/
            // value_font on arc; picked a neutral gray since the scrollbar isn't
            // actually rendered in the designer canvas to visually verify against
            // — reasonable placeholder, not a verified firmware-matching value.
            scrollbar: { bg_color: '#808080' },
        },
    },
    states: ['pressed', 'disabled'],
    editorCapabilities:  ['can_transform', 'can_copy'],
    runtimeCapabilities: ['has_action'],

    events: SELECTION_EVENTS,

    convertTo: ['roller'],

    bindings: {
        properties: { val: {} },
        actions: { hass_services: ['', 'homeassistant', 'openhasp', 'input_select', 'esphome'] },
    },

    automation: {
        runtimeVariables: [
            { name: 'text', type: 'string', label: 'Selected Option', optionsFrom: 'options' },
            { name: 'val',  type: 'int',    label: 'Selected Option Index' },
        ],
        actionDomains: ['input_select', 'light', 'switch', 'climate', 'cover', 'fan', 'homeassistant', 'openhasp', 'esphome'],
    },
}
